import { resolveReviewMediaUrl } from '../lib/media'

import { ReviewListMode } from './constants'
import { ProductReview, ProductReviewsResponse, ProductReviewVote, ReviewMediaItem } from './types'

export const mergeReviewsById = (currentReviews: ProductReview[], nextReviews: ProductReview[]) =>
	Array.from(new Map([...currentReviews, ...nextReviews].map(review => [review.id, review])).values())

export const getVisibleReviews = (reviews: ProductReview[], listMode: ReviewListMode) => {
	return [...reviews].sort((first, second) => {
		if (listMode === 'rating-low') return first.rating - second.rating
		if (listMode === 'rating-high') return second.rating - first.rating
		return 0
	})
}

export const getReviewMediaItems = (data: ProductReviewsResponse): ReviewMediaItem[] => data.mediaItems ?? []

export const getReviewMediaOwnerId = (item: ReviewMediaItem) => item.reviewId ?? item.review.id

export const getReviewWithOrderedPhotos = (review: ProductReview, mediaItems: ReviewMediaItem[]) => {
	const seen = new Set<string>()
	const photos = mediaItems.reduce<string[]>((result, item) => {
		if (getReviewMediaOwnerId(item) !== review.id || seen.has(item.src)) return result

		seen.add(item.src)
		return [...result, item.src]
	}, [])

	review.photos.forEach(src => {
		if (seen.has(src)) return

		seen.add(src)
		photos.push(src)
	})

	return { ...review, photos }
}

export const alignReviewsMediaOrder = (reviews: ProductReview[], mediaItems: ReviewMediaItem[]) => {
	const reviewsById = new Map<string, ProductReview>()
	const alignedReviews = reviews.map(review => {
		const alignedReview = getReviewWithOrderedPhotos(review, mediaItems)
		reviewsById.set(alignedReview.id, alignedReview)
		return alignedReview
	})
	const alignedMediaItems = mediaItems.map(item => {
		const review = reviewsById.get(getReviewMediaOwnerId(item)) ?? getReviewWithOrderedPhotos(item.review, mediaItems)
		const photoIndex = review.photos.findIndex(src => src === item.src)

		return {
			...item,
			review,
			photoIndex: photoIndex >= 0 ? photoIndex : item.photoIndex
		}
	})

	return { reviews: alignedReviews, mediaItems: alignedMediaItems }
}

export const findReviewMediaIndex = (items: ReviewMediaItem[], reviewId: string, photoIndex: number, src?: string) => {
	const indexBySrc = src
		? items.findIndex(
				item =>
					(item.reviewId ?? item.review.id) === reviewId &&
					resolveReviewMediaUrl(item.src) === resolveReviewMediaUrl(src)
		  )
		: -1

	return indexBySrc >= 0
		? indexBySrc
		: items.findIndex(item => (item.reviewId ?? item.review.id) === reviewId && item.photoIndex === photoIndex)
}

export const updateReviewVoteState = (
	review: ProductReview,
	vote: ProductReviewVote,
	updatedReview?: ProductReview
) => {
	if (updatedReview) {
		return {
			...review,
			likes: updatedReview.likes,
			dislikes: updatedReview.dislikes,
			userVote: updatedReview.userVote
		}
	}

	const isRemovingVote = review.userVote === vote
	const likes = review.likes - (review.userVote === 'like' ? 1 : 0) + (!isRemovingVote && vote === 'like' ? 1 : 0)
	const dislikes =
		review.dislikes - (review.userVote === 'dislike' ? 1 : 0) + (!isRemovingVote && vote === 'dislike' ? 1 : 0)

	return {
		...review,
		likes: Math.max(0, likes),
		dislikes: Math.max(0, dislikes),
		userVote: isRemovingVote ? null : vote
	}
}

export const updateReviewsResponseVoteState = (
	data: ProductReviewsResponse,
	reviewId: string,
	vote: ProductReviewVote,
	updatedReview?: ProductReview
): ProductReviewsResponse => ({
	...data,
	reviews: data.reviews.map(review =>
		review.id === reviewId ? updateReviewVoteState(review, vote, updatedReview) : review
	),
	mediaItems: data.mediaItems?.map(item =>
		item.review.id === reviewId || item.reviewId === reviewId
			? { ...item, review: updateReviewVoteState(item.review, vote, updatedReview) }
			: item
	),
	mediaReviews: data.mediaReviews?.map(review =>
		review.id === reviewId ? updateReviewVoteState(review, vote, updatedReview) : review
	)
})
